import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export interface DashboardStats {
  tenants: { total: number };
  sessions: {
    total: number;
    completed: number;
    pass: number;
    review: number;
    fail: number;
    by_status: { status: string; count: number }[];
  };
  kpis: {
    active_agents: number;
    agents_new_this_month: number;
    agents_new_prev_month: number;
    pd_today: number;
    pd_yesterday: number;
    accuracy_this_week: number | null;
    accuracy_prev_week: number | null;
    pending_reviews: number;
    pending_reviews_yesterday: number;
    avg_duration_this_week: number | null;
    avg_duration_prev_week: number | null;
  };
  cache_generated_at: string;
}

export interface WeeklyActivityData {
  labels: string[];
  data: number[];
  period: string;
}

export interface AgentPerformanceItem {
  id: number;
  uuid: string;
  name: string;
  agent_code: string | null;
  status: string;
  total_pds: number;
  accuracy: string;
}

export interface RecentActivityItem {
  id: number;
  session_code: string | null;
  customer_name: string;
  agent_name: string;
  agent_code: string | null;
  tenant_id: number | null;
  session_source: 'platform' | 'tenant';
  status: string;
  result: string | null;
  score: number | null;
  created_at: string;
}

export interface TenantDropdownItem {
  id: number;
  tenant_name: string;
  tenant_subdomain: string;
}

export interface ActivitiesListData {
  data: RecentActivityItem[];
  total: number;
  page: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class SuperAdminDashboardService {
  private readonly base = `${environment.apiBaseUrl.replace(/\/$/, '')}/super-admin/dashboard`;

  constructor(private readonly http: HttpClient) {}

  getStats(tenantId?: number): Observable<ApiEnvelope<DashboardStats>> {
    const params: Record<string, string> = {};
    if (tenantId) params['tenant_id'] = String(tenantId);
    return this.http.get<ApiEnvelope<DashboardStats>>(`${this.base}/stats`, { params });
  }

  getWeeklyActivity(tenantId?: number, period = '30d'): Observable<ApiEnvelope<WeeklyActivityData>> {
    const params: Record<string, string> = { period };
    if (tenantId) params['tenant_id'] = String(tenantId);
    return this.http.get<ApiEnvelope<WeeklyActivityData>>(`${this.base}/weekly-activity`, { params });
  }

  getAgentPerformance(tenantId?: number, period = '30d'): Observable<ApiEnvelope<AgentPerformanceItem[]>> {
    const params: Record<string, string> = { period };
    if (tenantId) params['tenant_id'] = String(tenantId);
    return this.http.get<ApiEnvelope<AgentPerformanceItem[]>>(`${this.base}/agent-performance`, { params });
  }

  getRecentActivity(tenantId?: number): Observable<ApiEnvelope<RecentActivityItem[]>> {
    const params: Record<string, string> = {};
    if (tenantId) params['tenant_id'] = String(tenantId);
    return this.http.get<ApiEnvelope<RecentActivityItem[]>>(`${this.base}/recent-activity`, { params });
  }

  getActivities(params: {
    tenantId?: number;
    search?: string;
    result?: string;
    page?: number;
    pageSize?: number;
  }): Observable<ApiEnvelope<ActivitiesListData>> {
    const q: Record<string, string> = {};
    if (params.tenantId) q['tenant_id'] = String(params.tenantId);
    if (params.search) q['search'] = params.search;
    if (params.result) q['result'] = params.result;
    if (params.page) q['page'] = String(params.page);
    if (params.pageSize) q['pageSize'] = String(params.pageSize);
    return this.http.get<ApiEnvelope<ActivitiesListData>>(`${this.base}/activities`, { params: q });
  }

  getTenants(): Observable<ApiEnvelope<TenantDropdownItem[]>> {
    return this.http.get<ApiEnvelope<TenantDropdownItem[]>>(`${this.base}/tenants`);
  }
}
